import { useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Slider,
  Typography,
  Box,
} from "@mui/material";
import { useForm, Controller, type Control } from "react-hook-form";
import { useTranslation } from "react-i18next";
import type { Player } from "@/types";

interface PlayerFormData {
  name: string;
  defense: number;
  agility: number;
  technique: number;
}

interface PlayerDialogProps {
  open: boolean;
  player?: Player | null;
  onClose: () => void;
  onSubmit: (data: PlayerFormData) => void;
}

function StatSlider({
  name,
  control,
  label,
}: {
  name: keyof Pick<PlayerFormData, "defense" | "agility" | "technique">;
  control: Control<PlayerFormData>;
  label: string;
}) {
  return (
    <Box>
      <Typography gutterBottom>{label}</Typography>
      <Controller
        name={name}
        control={control}
        render={({ field }) => (
          <Slider
            {...field}
            min={1}
            max={10}
            step={1}
            marks
            valueLabelDisplay="auto"
            onChange={(_, val) => field.onChange(val)}
          />
        )}
      />
    </Box>
  );
}

export function PlayerDialog({
  open,
  player,
  onClose,
  onSubmit,
}: PlayerDialogProps) {
  const { t } = useTranslation();
  const { control, handleSubmit, reset } = useForm<PlayerFormData>({
    defaultValues: { name: "", defense: 5, agility: 5, technique: 5 },
  });

  useEffect(() => {
    if (player) {
      reset({
        name: player.name,
        defense: player.defense,
        agility: player.agility,
        technique: player.technique,
      });
    } else {
      reset({ name: "", defense: 5, agility: 5, technique: 5 });
    }
  }, [player, open, reset]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogTitle>
          {player ? t("common.edit") : t("common.add")} {t("players.player")}
        </DialogTitle>
        <DialogContent
          sx={{ display: "flex", flexDirection: "column", gap: 3, pt: 2 }}
        >
          <Controller
            name="name"
            control={control}
            rules={{ required: true }}
            render={({ field }) => (
              <TextField
                {...field}
                label={t("players.name")}
                fullWidth
                autoFocus
              />
            )}
          />
          <StatSlider
            name="defense"
            control={control}
            label={t("players.defense")}
          />
          <StatSlider
            name="agility"
            control={control}
            label={t("players.agility")}
          />
          <StatSlider
            name="technique"
            control={control}
            label={t("players.technique")}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>{t("common.cancel")}</Button>
          <Button type="submit" variant="contained">
            {t("common.save")}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
